import { useMemo, useState } from 'react';
import { useTranslation } from 'react-i18next';
import { useQuery, useQueryClient } from '@tanstack/react-query';
import {
  Avatar,
  Box,
  Button,
  Chip,
  CircularProgress,
  Container,
  InputAdornment,
  Paper,
  Stack,
  TextField,
  Typography,
} from '@mui/material';
import DeleteOutlineIcon from '@mui/icons-material/DeleteOutline';
import SearchIcon from '@mui/icons-material/Search';
import StorefrontIcon from '@mui/icons-material/Storefront';
import {
  adminUsersService,
  type AdminUserSummary,
} from '../../shared/services/adminUsers.service';
import { useAuth } from '../../shared/hooks/useAuth';
import { useNotification } from '../../shared/notifications/NotificationProvider';
import { ConfirmDeleteByEmailDialog } from './ConfirmDeleteByEmailDialog';

/**
 * SUPER_ADMIN-only delete UI. ADMIN can browse the list (read-only) but
 * the delete button only shows for SUPER_ADMIN and never for the row that
 * matches the current user (no self-delete from the console). The backend
 * enforces both rules independently.
 */
export function AdminUsersPage() {
  const { t } = useTranslation();
  const { user, hasRole } = useAuth();
  const qc = useQueryClient();
  const { notify } = useNotification();
  const [q, setQ] = useState('');
  const [deleting, setDeleting] = useState<AdminUserSummary | null>(null);
  const canDelete = hasRole('SUPER_ADMIN');

  const list = useQuery({
    queryKey: ['admin', 'users', q],
    queryFn: () => adminUsersService.list({ q, page: 0, size: 50 }),
  });

  // Deletion is gated by an emailed confirmation code — the dialog runs
  // the full request/confirm flow and reports success here.
  const onDeleteSuccess = () => {
    qc.invalidateQueries({ queryKey: ['admin', 'users'] });
    qc.invalidateQueries({ queryKey: ['admin', 'providers'] });
    notify.success(t('adminUsers.deleted'));
    setDeleting(null);
  };

  const items: AdminUserSummary[] = list.data?.data ?? [];

  const rows = useMemo(
    () => items.map((u) => ({ ...u, isSelf: user?.id === u.id })),
    [items, user?.id],
  );

  return (
    <Container maxWidth="lg" sx={{ py: { xs: 2, md: 4 } }}>
      <Stack direction={{ xs: 'column', sm: 'row' }} spacing={2} alignItems={{ sm: 'center' }} sx={{ mb: 3 }}>
        <Typography variant="h4" sx={{ fontWeight: 700, flexGrow: 1 }}>
          {t('adminUsers.title')}
        </Typography>
        <TextField
          size="small"
          placeholder={t('adminUsers.searchPlaceholder')}
          value={q}
          onChange={(e) => setQ(e.target.value)}
          sx={{ minWidth: { xs: '100%', sm: 280 } }}
          InputProps={{
            startAdornment: (
              <InputAdornment position="start">
                <SearchIcon fontSize="small" />
              </InputAdornment>
            ),
          }}
        />
      </Stack>

      {list.isLoading ? (
        <Box sx={{ display: 'flex', justifyContent: 'center', py: 6 }}>
          <CircularProgress />
        </Box>
      ) : rows.length === 0 ? (
        <Typography color="text.secondary">{t('adminUsers.empty')}</Typography>
      ) : (
        <Stack spacing={1.5}>
          {rows.map((u) => (
            <Paper key={u.id} variant="outlined" sx={{ p: 2, borderRadius: 2 }}>
              <Stack
                direction={{ xs: 'column', sm: 'row' }}
                spacing={2}
                alignItems={{ sm: 'center' }}
              >
                <Avatar>{(u.displayName || u.email).slice(0, 1).toUpperCase()}</Avatar>
                <Box sx={{ flexGrow: 1, minWidth: 0 }}>
                  <Typography variant="subtitle1" sx={{ fontWeight: 600 }} noWrap>
                    {u.displayName}
                  </Typography>
                  <Typography variant="body2" color="text.secondary" noWrap>
                    {u.email}
                  </Typography>
                  <Stack direction="row" spacing={0.75} sx={{ mt: 0.75, flexWrap: 'wrap', rowGap: 0.5 }}>
                    {u.roles.map((r) => (
                      <Chip key={r} label={r} size="small" variant="outlined" />
                    ))}
                    {u.isProvider && (
                      <Chip
                        icon={<StorefrontIcon fontSize="small" />}
                        label={u.providerBusinessName ?? t('adminUsers.providerBadge')}
                        size="small"
                        color="primary"
                        variant="outlined"
                      />
                    )}
                    {u.isSelf && (
                      <Chip label={t('adminUsers.youBadge')} size="small" color="info" />
                    )}
                  </Stack>
                </Box>
                {canDelete && !u.isSelf && (
                  <Button
                    variant="outlined"
                    color="error"
                    startIcon={<DeleteOutlineIcon />}
                    onClick={() => setDeleting(u)}
                  >
                    {t('common.delete')}
                  </Button>
                )}
              </Stack>
            </Paper>
          ))}
        </Stack>
      )}

      {deleting && (
        <ConfirmDeleteByEmailDialog
          open={Boolean(deleting)}
          onClose={() => setDeleting(null)}
          onSuccess={onDeleteSuccess}
          title={t('adminUsers.confirmTitle')}
          targetLabel={`${deleting.displayName} (${deleting.email})`}
          requestCode={() => adminUsersService.requestDelete(deleting.id)}
          confirm={(code) => adminUsersService.remove(deleting.id, code)}
        />
      )}
    </Container>
  );
}
